// inherited from FilterManagerCommon but simple

import moment from 'moment';

import { tabledata, timeformat } from '#public/commons-global';
import type { DbOrderJsRuntime } from '#public_ts/api';
import type { BinaryOperationType, Condition, OrderType, ValueTypeDef } from '#public_ts/commons/dbs-shared';
import type { ProbablyString } from '#public_ts/commons/fe-commons';
import { typeSafeObjectEntries } from '#public_ts/commons/fe-commons';

export type SimpleFilterManagerDef<
    D extends object,
    T extends Exclude<keyof D, symbol | number> = Exclude<keyof D, symbol | number>,
> = {
    textbox?: boolean;
    op?: BinaryOperationType | ((value: string | string[], def: SimpleFilterManagerDef<D, T>) => BinaryOperationType);
    type?: ValueTypeDef;
    nullable?: boolean;
    fn?: (
        value: string | string[],
        def: SimpleFilterManagerDef<D, T>,
        single_column: T,
        op: BinaryOperationType,
        type: ValueTypeDef
    ) => (Condition<T>[] | void | undefined);
};

export type SimpleFilterManagerDefs<
    D extends object,
    T extends Exclude<keyof D, symbol | number> = Exclude<keyof D, symbol | number>,
> = Partial<Record<T, SimpleFilterManagerDef<D, T>>>;

export function parseDateFilter(value: string, type: ValueTypeDef) {
    const parts = value.split(/\s*\/\s*/);
    const partsOrder = ['year', 'month', 'day'] as const;
    if(!parts.length || (parts.length > partsOrder.length)) return undefined;

    const dateParts: Partial<Record<typeof partsOrder[number], number>> = {};
    let idx = 0;
    for(let i = parts.length - 1; i >= 0; i--) {
        const part = Number(parts[i]);
        if(Number.isNaN(part)) return undefined;
        dateParts[partsOrder[idx++]!] = part;
    }

    //month is zero based
    if(dateParts.month !== undefined) dateParts.month--;

    let dateStart;
    let dateFinish;
    if(dateParts.year !== undefined) {
        if(dateParts.month === undefined) {
            dateStart = moment.utc([dateParts.year]);
            dateFinish = dateStart.clone().endOf('year');
        } else if(dateParts.day === undefined) {
            dateStart = moment.utc([dateParts.year, dateParts.month]);
            dateFinish = dateStart.clone().endOf('month');
        } else {
            dateStart = moment.utc([dateParts.year, dateParts.month, dateParts.day]);
            dateFinish = dateStart.clone().endOf('day');
        }
    }
    if(!dateStart.isValid() || !dateFinish.isValid()) return undefined;

    const needTime = (type == 'dateTime');
    if(needTime) {
        dateStart = moment(dateStart.utc().toArray());
        dateFinish = moment(dateFinish.utc().toArray());
    }

    return {
        from: new timeformat(dateStart).toXml({ needTime, zone: needTime }),
        to: new timeformat(dateFinish).toXml({ needTime, zone: needTime }),
    };
}

export function useSimpleFilterManager<
    D extends object,
    T extends Exclude<keyof D, symbol | number> = Exclude<keyof D, symbol | number>,
>(
    defs: SimpleFilterManagerDefs<D, T>,
    handleFilter?: (
        def: SimpleFilterManagerDef<D, T>,
        single_column: string,
        op: BinaryOperationType,
        value: string,
        type: ValueTypeDef,
        single_filter: Condition<T>[],
    ) => (Condition<T>[] | void | undefined),
    options: { textbox?: boolean } = {},
) {
    if(options.textbox) {
        Object.values(defs).forEach((def) => { (def as SimpleFilterManagerDef<D, T>).textbox = true; });
    }

    function buildDateFilter(column: T, value: string, type: ValueTypeDef): (Condition<T>[] | undefined) {
        const time = parseDateFilter(value, type);
        if(!time) return undefined;

        const filter2: Condition<T>[] = [['and',
            [column, '>=', time.from, type],
            [column, '<=', time.to, type],
        ]];
        return filter2;
    }

    function buildFilterFromData(
        def: SimpleFilterManagerDef<D, T>,
        single_column: T,
        op: BinaryOperationType,
        value: string,
        type: ValueTypeDef,
    ): (Condition<T>[] | undefined) {
        let single_filter: Condition<T>[] | undefined;
        if(def.textbox && ((type == 'date') || (type == 'dateTime'))) {
            single_filter = buildDateFilter(single_column, value, type);
        } else {
            if(def.nullable) {
                if(value == '----') {
                    single_filter = [[single_column, 'is null']];
                } else if(value == '++++') {
                    single_filter = [[single_column, 'is not null']];
                }
            }
            if(!single_filter) {
                single_filter = [[single_column, op, value, type]];
            }
        }
        return single_filter;
    }

    function buildSimpleFilter(filter: Partial<Record<T, string | string[]>>): Condition<T>[] {
        const resultFilter: Condition<T>[] = [];
        for(const [data, value0] of typeSafeObjectEntries(filter as Record<T, string | string[]>)) {
            let value = value0;
            if(value && (typeof (value) == 'string')) value = value.trim();
            if(!value) continue;

            const def = defs[data];
            if(!def) continue;

            let op = def.op || '=';
            if(typeof (op) == 'function') {
                op = op(value, def);
            }
            const type = def.type || 'string';
            if(op == 'like') {
                value = String(value);
                if(def.nullable && ((value == '----') || (value == '++++'))) {
                //specials
                } else {
                    value = tabledata.fixFilterLike(value);
                }
            }

            const single_column = data;
            let single_filter: Condition<T>[] | void | undefined;
            if(def.fn) {
                single_filter = def.fn(value, def, single_column, op, type);
            } else {
                value = String(value);
                single_filter = buildFilterFromData(def, single_column, op, value, type);
                if(single_filter && handleFilter) {
                    single_filter = handleFilter(def, single_column, op, value, type, single_filter);
                }
            }
            if(single_filter) {
                resultFilter.push(...single_filter);
            }
        }

        return resultFilter;
    }

    // it was written to replace fatturaFormIpa.js - buildFilter and common.ts - applyTableFilter
    // it is based on descriptors, but for simpler usage with just text selection
    function buildSimpleTextboxFilter(
        filterValue: ProbablyString,
        { spaceSeparatedArray }: {
            spaceSeparatedArray?: boolean;
        } = {},
    ): Condition<T>[] {
        const resultFilter: Condition<T>[] = [];

        filterValue = filterValue?.trim();
        if(!filterValue) {
            return resultFilter;
        }

        const filterArray = spaceSeparatedArray ? filterValue.split(/[\s,]+/) : [filterValue];
        for(const value of filterArray) {
            let haveDate = false;
            let haveNumber = false;
            const num = Number(value);
            if(!isNaN(num)) {
                if(num <= 2100) {
                    haveDate = true;
                }
                haveNumber = true;
            }
            if(isNaN(num)) {
                // don't try to parse again if was number
                const timeFilter = parseDateFilter(value, 'dateTime');
                if(timeFilter) {
                    haveDate = true;
                }
            }

            const filterObject: Partial<Record<T, string | string[]>> = {};
            for(const [key, _def] of Object.entries(defs)) {
                const def = _def as SimpleFilterManagerDef<D, T>;
                if((def.type === 'date') || def.type === 'dateTime') {
                    if(haveDate) filterObject[key] = value;
                } else if(def.type === 'long') {
                    if(haveNumber) filterObject[key] = value;
                } else {
                    filterObject[key] = value;
                }
            }

            resultFilter.push({ or: buildSimpleFilter(filterObject) });
        }

        return resultFilter;
    }

    // see common.ts - applyTableOrder
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function buildRuntimeOrderFromDatatable(data: any) {
        const order: DbOrderJsRuntime<T>[] = [];
        for(const o of data.order) {
            let column = data.columns[o.column];
            column = column.data;
            const odef: DbOrderJsRuntime<T> = {
                column: column as T,
                desc: o.dir == 'desc',
            };
            order.push(odef);
        }
        return order;
    }

    function buildOrderFromRuntime(data: DbOrderJsRuntime<T>[] | undefined) {
        const order = (data ?? []).map((c) => ({ [c.column]: c.desc ? 'desc' : 'asc' } as const));
        return order as OrderType<T>;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function buildOrderFromDatatable(data: any) {
        return buildOrderFromRuntime(buildRuntimeOrderFromDatatable(data));
    }

    return {
        buildSimpleFilter,
        buildSimpleTextboxFilter,
        buildRuntimeOrderFromDatatable,
        buildOrderFromRuntime,
        buildOrderFromDatatable,
    };
}
